import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { InformationalScreen, LoadingModal } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useAccount, useApiError, useAuth, useClickContinueEmailVerificationButton, useResendVerificationEmail, } from 'Hooks';
import CheckYourEmailComponents from 'Components/register/CheckYourEmail/components';
import { Footer, MainContainer, TopHeaderBackArrow } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { validateFrenchZipCode } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { signIn } from 'Services/auth';
import { registerPatient } from 'Services/patient';
import { ContentContainer, createYupSchema, initialState } from './helpers';
import { RegisterAccountForm, RegisterAdultAccount, RegisterConfirmCountry, RegisterMinorAccount, RegisterMinorAccountInfo, RegisterMinorAccountIntro, } from './steps';
const PageIndexes = {
    REGISTER_CONFIRM_COUNTRY: 0,
    REGISTER_ACCOUNT_FORM: 1,
    REGISTER_ADULT_ACCOUNT: 2,
    REGISTER_MINOR_ACCOUNT_INTRO: 3,
    REGISTER_MINOR_ACCOUNT_INFO: 4,
    REGISTER_MINOR_ACCOUNT: 5,
    REGISTER_CHECK_EMAIL: 6,
};
const mapStateToProps = ({ auth: { account_id }, env: { deviceId, installationId, osType, osVersion, app, appVersion, country, phoneLanguage, appHash, }, config: { validation, minAge, passwordRules, supportedCountries, countries }, }) => {
    return {
        account_id,
        deviceId,
        installationId,
        osType,
        osVersion,
        app,
        appVersion,
        validation,
        minAge,
        firstNameLimit: validation.patient.firstNameMaxLength,
        lastNameLimit: validation.patient.lastNameMaxLength,
        country,
        phoneLanguage,
        appHash,
        supportedCountries,
        countries,
        passwordRules,
    };
};
const connector = connect(mapStateToProps);
const CreateNewUser = ({ account_id, minAge, app, appVersion, country, deviceId, installationId, firstNameLimit, lastNameLimit, osType, osVersion, phoneLanguage, appHash, countries, supportedCountries, passwordRules, }) => {
    const isLLU = app === 'llu';
    const [loading, setLoading] = useState(false);
    const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState(isLLU ? PageIndexes.REGISTER_CONFIRM_COUNTRY : PageIndexes.REGISTER_ACCOUNT_FORM);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const { showEmailExistsMessage, showExistingProUserError, showPasswordValidationError, showMinorRestrictionMessage, validateEntriesLengthAndDisplayError, validateIsMinor, } = useAccount();
    const { handleAndroidIntegrityError } = useAuth();
    const validationSchema = useMemo(() => createYupSchema(currentPageIndex, app, isLLU), [currentPageIndex, app, isLLU]);
    const formController = useFormik({
        initialValues: { ...initialState, country: country || (isLLU ? '' : 'US') },
        validationSchema: validationSchema,
        validateOnBlur: true,
        onSubmit: (values) => handleSubmitRegister(values),
    });
    const { handleSubmit, setValues, setTouched, setErrors, setFieldError, isValid, isSubmitting, values, touched, } = formController;
    const showPhoneInput = !isLLU && values.country === 'US';
    const handleClickSignIn = useCallback(() => {
        /*
          Need to use the redirect method to replaces the current location in browser history.
          When the user clicks the back button on the login page, it should redirect the user back to the app.
        */
        mediator.publish('router:redirect', '/login');
    }, []);
    const validateNameLength = useCallback((firstName, lastName) => {
        const isValid = validateEntriesLengthAndDisplayError([
            {
                value: firstName,
                msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
                count: firstNameLimit,
            },
            {
                value: lastName,
                msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
                count: lastNameLimit,
            },
        ]);
        return isValid;
    }, [firstNameLimit, lastNameLimit, validateEntriesLengthAndDisplayError]);
    const handleApiRequestError = useCallback((error) => {
        const data = error.response?.data
            ? error.response.data
            : {
                details: error.code === ErrorCode.NETWORK_ERROR
                    ? ErrorCode.NETWORK_ERROR
                    : i18n.t('Global.modals.errorCommunicatingServer.body'),
            };
        notifyApp('adc-webview:create-account', { status: error.status, data });
        switch (error.status) {
            case ApiStatus.CONFLICT: {
                const code = error.response?.data.code;
                if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
                    showExistingProUserError();
                    return;
                }
                showEmailExistsMessage(handleClickSignIn);
                break;
            }
            case ApiStatus.BAD_REQUEST: {
                const code = error.response?.data.code;
                const errors = error.response?.data.errors;
                const errorDetail = errors?.[0];
                if (errorDetail?.key === 'phoneNumber' && errorDetail.error === 'invalidInput') {
                    setFieldError('mobileNumber', i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.validPhoneNumber'));
                    return;
                }
                if (code === ApiStatus.PASSWORD_VALIDATION) {
                    showPasswordValidationError(passwordRules, errors, values.country);
                    return;
                }
                if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
                    handleAndroidIntegrityError(error);
                    return;
                }
                showApiErrorModal();
                break;
            }
            case ApiStatus.UNAUTHORIZED: {
                const code = error.response?.data.code;
                if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
                    handleAndroidIntegrityError(error);
                    return;
                }
                showApiErrorModal();
                break;
            }
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [
        handleAndroidIntegrityError,
        handleClickSignIn,
        passwordRules,
        setFieldError,
        showApiErrorModal,
        showEmailExistsMessage,
        showExistingProUserError,
        showNetworkErrorModal,
        showPasswordValidationError,
        values.country,
    ]);
    const signInUser = useCallback(async ({ email, password, callback, country, }) => {
        const authSession = await signIn({
            email: email,
            password: password,
            osType,
            osVersion,
            app,
            appVersion,
            deviceId,
            installationId,
            appHash,
        });
        store.dispatch({ type: AUTH_SESSION, authSession });
        if (!isLLU) {
            if (country === 'US') {
                mediator.publish('router:navigate', '/hipaa');
                return callback?.();
            }
            mediator.publish('router:navigate', '/rwe');
            return callback?.();
        }
        else {
            setCurrentPageIndex(PageIndexes.REGISTER_CHECK_EMAIL);
        }
        if (!authSession.authentication_steps) {
            notifyApp('adc-webview:signin', authSession);
        }
        callback?.();
        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
            setLoading(false);
        }, APP_RETURN_TIMEOUT);
    }, [app, appHash, appVersion, deviceId, installationId, isLLU, osType, osVersion]);
    const handleSubmitRegister = useCallback((register) => {
        return new Promise((resolve, reject) => {
            setLoading(true);
            const newRegister = {
                ...register,
                ...values,
            };
            registerPatient(newRegister, osType, osVersion, app, appVersion, phoneLanguage, appHash)
                .then(() => notifyApp('adc-webview:create-account', { accountCreated: true }))
                .then(async () => signInUser({
                email: newRegister.email,
                password: newRegister.password,
                callback: resolve,
                country: newRegister.country,
            }))
                .catch((error) => {
                handleApiRequestError(error);
                reject(error);
            })
                .finally(() => {
                setLoading(false);
            });
        });
    }, [
        values,
        osType,
        osVersion,
        app,
        appVersion,
        phoneLanguage,
        appHash,
        signInUser,
        handleApiRequestError,
    ]);
    const { handleResendEmailButtonClick } = useResendVerificationEmail({
        account_id,
        osType,
        osVersion,
        app,
        appVersion,
        handleApiRequestError,
    });
    const signInFunction = () => {
        signInUser({
            email: values.email,
            password: values.password?.trim(),
        });
    };
    const { handleContinueEmailVerificationButtonClick } = useClickContinueEmailVerificationButton({
        account_id,
        osType,
        osVersion,
        app,
        appVersion,
        installationId,
        handleApiRequestError,
        signInFunction,
    });
    const onNextPressed = useCallback(() => {
        switch (currentPageIndex) {
            case PageIndexes.REGISTER_CONFIRM_COUNTRY:
                setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
                break;
            case PageIndexes.REGISTER_ACCOUNT_FORM: {
                if (values.country === 'FR' && values.birthCountry === 'FR') {
                    const isZipValid = validateFrenchZipCode(values.birthZipCode);
                    if (!isZipValid) {
                        setFieldError('birthZipCode', i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.validZipCode'));
                        return;
                    }
                }
                const isMinor = validateIsMinor(values.dateOfBirth, minAge);
                const isNameValid = validateNameLength(values.firstName, values.lastName);
                if (!isNameValid) {
                    return;
                }
                setValues({ ...values, isMinor }, false);
                if (!isMinor) {
                    setValues({ ...values, password: '', confirmPassword: '' }, false);
                    setTouched({ ...touched, password: false, confirmPassword: false }, false);
                    setCurrentPageIndex(PageIndexes.REGISTER_ADULT_ACCOUNT);
                    return;
                }
                // no minors for 'llu'
                if (isLLU) {
                    showMinorRestrictionMessage(minAge);
                    return;
                }
                // user is minor, handle another flow: show minor related info
                setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO);
                break;
            }
            case PageIndexes.REGISTER_ADULT_ACCOUNT:
                handleSubmit();
                break;
            case PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO:
                setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INFO);
                break;
            case PageIndexes.REGISTER_MINOR_ACCOUNT_INFO: {
                const isNameValid = validateNameLength(values.guardianFirstName, values.guardianLastName);
                if (!isNameValid) {
                    return;
                }
                setValues({ ...values, password: '', confirmPassword: '' }, false);
                setTouched({ ...touched, password: false, confirmPassword: false }, false);
                setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT);
                break;
            }
            case PageIndexes.REGISTER_MINOR_ACCOUNT:
                handleSubmit();
                break;
            default:
                break;
        }
    }, [
        currentPageIndex,
        handleSubmit,
        values,
        validateIsMinor,
        minAge,
        validateNameLength,
        setValues,
        isLLU,
        setFieldError,
        setTouched,
        touched,
        showMinorRestrictionMessage,
    ]);
    const handleBackPressed = useCallback(() => {
        switch (currentPageIndex) {
            case PageIndexes.REGISTER_ACCOUNT_FORM:
                if (app !== 'llu') {
                    notifyApp('adc-webview:navigate-back-app');
                }
                else {
                    setCurrentPageIndex(PageIndexes.REGISTER_CONFIRM_COUNTRY);
                }
                break;
            case PageIndexes.REGISTER_ADULT_ACCOUNT:
                setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
                break;
            case PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO:
                setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
                break;
            case PageIndexes.REGISTER_MINOR_ACCOUNT_INFO:
                setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO);
                break;
            case PageIndexes.REGISTER_MINOR_ACCOUNT:
                setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INFO);
                break;
            case PageIndexes.REGISTER_CHECK_EMAIL:
                setCurrentPageIndex(PageIndexes.REGISTER_ADULT_ACCOUNT);
                break;
            default:
                notifyApp('adc-webview:navigate-back-app');
                break;
        }
        // fix for formik remembering state from before pressing BACK arrow
        setErrors({});
    }, [app, currentPageIndex, setErrors]);
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, { topNavigationProps: {
                title: i18n.t('CreateAccountPersonalInfo.title'),
                animatedTitle: true,
                buttonStart: {
                    element: React.createElement(TopHeaderBackArrow, { testID: "CreateAccount.backButton" }),
                    label: 'Back',
                    onPress: handleBackPressed,
                },
                testID: 'CreateAccount.topHeader',
            }, index: currentPageIndex, testID: "CreateAccount" },
            React.createElement(InformationalScreen.Page, { Content: React.createElement(RegisterConfirmCountry, { countries: supportedCountries, formikForm: formController, setLoading: setLoading }), Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('Global.microcopy.common.next'), onButtonPress: onNextPressed, buttonTestID: "RegisterConfirmCountry.nextButton", disabled: !values.country || !values.confirmCountryChecked || !isValid || isSubmitting, secondarySectionLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'), onSecondaryButtonPress: handleClickSignIn, secondaryButtonTestID: "RegisterConfirmCountry.signIn", secondaryButtonLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label') }) }),
            React.createElement(InformationalScreen.Page, { Content: React.createElement(RegisterAccountForm, { isLLU: isLLU, countries: countries, formikForm: formController }), Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'), onButtonPress: onNextPressed, buttonTestID: "RegisterPersonalInfo.nextButton", secondarySectionLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'), onSecondaryButtonPress: handleClickSignIn, secondaryButtonTestID: "RegisterPersonalInfo.signIn", secondaryButtonLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'), disabled: !values.firstName ||
                        !values.lastName ||
                        !values.dateOfBirth ||
                        !isValid ||
                        isSubmitting }) }),
            React.createElement(InformationalScreen.Page, { Content: React.createElement(RegisterAdultAccount, { showPhoneInput: showPhoneInput, formikForm: formController, passwordRules: passwordRules, onValidate: setMeetsPasswordRequirements }), Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.links.next.label'), onButtonPress: handleSubmit, buttonTestID: "RegisterAccountInfo.nextButton", secondarySectionLabel: i18n.t('CreateAccountAccountInfo.content.footerSignIn.primaryText'), onSecondaryButtonPress: handleClickSignIn, secondaryButtonTestID: "RegisterAccountInfo.signIn", secondaryButtonLabel: i18n.t('CreateAccountAccountInfo.content.footerSignIn.links.signIn.label'), disabled: !values.email ||
                        !values.password ||
                        !values.confirmPassword ||
                        !isValid ||
                        isSubmitting ||
                        !meetsPasswordRequirements ||
                        (!isLLU && !values.mobileNumber) }) }),
            React.createElement(InformationalScreen.Page, { Content: RegisterMinorAccountIntro, Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('Global.microcopy.common.continue'), onButtonPress: () => onNextPressed(), buttonTestID: "CreateAccountIntro.continue-button", disabled: false }) }),
            React.createElement(InformationalScreen.Page, { Content: React.createElement(RegisterMinorAccountInfo, { formikForm: formController }), Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'), onButtonPress: () => onNextPressed(), buttonTestID: "CreateAccountPersonalInfo.nextButton", disabled: !values.guardianFirstName || !values.guardianLastName || !isValid || isSubmitting }) }),
            React.createElement(InformationalScreen.Page, { Content: React.createElement(RegisterMinorAccount, { showPhoneInput: showPhoneInput, formController: formController, passwordRules: passwordRules, onValidate: setMeetsPasswordRequirements }), Footer: React.createElement(Footer, { padding: "$4", buttonLabel: i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.links.next.label'), onButtonPress: handleSubmit, buttonTestID: "CreateAccountAccountInfo.nextButton", disabled: !values.email ||
                        !values.password ||
                        !values.confirmPassword ||
                        (!isLLU && !values.mobileNumber) ||
                        !isValid ||
                        isSubmitting ||
                        !meetsPasswordRequirements }) }),
            React.createElement(InformationalScreen.Page, { Content: React.createElement(ContentContainer, null,
                    React.createElement(CheckYourEmailComponents.Content, { email: formController?.values?.email, onButtonPress: handleResendEmailButtonClick })), Footer: React.createElement(Footer, { buttonLabel: i18n.t('Global.microcopy.common.continue'), onButtonPress: handleContinueEmailVerificationButtonClick, padding: "$4", buttonTestID: "CheckYourEmailRegistration.continueButton" }), topNavigationProps: {
                    title: i18n.t('CheckYourEmailRegistration.title'),
                    animatedTitle: true,
                    testID: 'CreateAccount.topHeader',
                    buttonStart: {
                        element: React.createElement(TopHeaderBackArrow, { testID: "CreateAccount.backButton" }),
                        onPress: handleBackPressed,
                    },
                } }))));
};
export default connector(CreateNewUser);
